import { Suspense, use } from 'react';

import { browser } from 'react-dom';

type NodeOrFn = React.ReactNode | (() => React.ReactNode);

type Props = {
	children?: NodeOrFn;
	placeholder?: NodeOrFn;
};

function RenderNodeOrFn({ nodeOrFn }: { nodeOrFn: NodeOrFn }): React.ReactElement | null {
	if (!nodeOrFn) return null;
	// casting necessary due to typing issue: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/18051
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
	return (typeof nodeOrFn === 'function' ? nodeOrFn() : nodeOrFn) as any;
}

function BrowserOnlyChildren({ children }: Pick<Props, 'children'>): React.ReactElement | null {
	// suspends server-side (see `browser`'s docs) - `children` is never invoked server-side, and this
	// never suspends client-side, so `children` renders immediately there, without an extra re-render
	use(browser());

	return <RenderNodeOrFn nodeOrFn={children} />;
}

/**
 * Can be used to render specific content only on the client
 *
 * By default, even if the component is not part of the generated DOM, it will still internally be rendered by React.
 *
 * For further optimization, the children and the placeholder can be functions so that that rendering doesn't happen.
 * This can be especially useful when using features that are not available server-side, or when rendering complex component trees.
 *
 * Internally, this relies on React's `use(browser())` (see `react-dom`) inside a `<Suspense>` boundary:
 * server rendering suspends and shows `placeholder` as the fallback, and the client never suspends, so
 * `children` render as soon as they're reached - no `firstRender` flag, no extra client-side re-render.
 */
export function ClientRender({ children, placeholder }: Props): React.ReactElement {
	return (
		<Suspense fallback={<RenderNodeOrFn nodeOrFn={placeholder} />}>
			<BrowserOnlyChildren>{children}</BrowserOnlyChildren>
		</Suspense>
	);
}
